<template>
  <div class="w-full bg-slate-50 min-h-screen p-4 md:p-8" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-2xl font-black text-slate-800 tracking-tight">Relatório de Abastecimentos Bloqueados</h1>
          <p class="text-sm text-slate-500 font-medium mt-1">Busque, gere relatórios e delete abastecimentos bloqueados</p>
        </div>
      </div>

      <!-- BUSCAR FORM -->
      <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
          <v-icon color="orange" small>mdi-filter-outline</v-icon>
          <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">FILTROS DE BUSCA</h2>
        </div>
        <div class="p-8">
          <v-row dense>
            <v-col cols="12" sm="12" md="3">
              <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Inicial</label>
              <Date @dateValue="[(filter.startDate = $event)]" />
            </v-col>
            <v-col cols="12" sm="12" md="3">
              <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Final</label>
              <Date @dateValue="[(filter.endDate = $event)]" />
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Prefeitura</label>
              <v-autocomplete
                v-model="filter.prefecture_id"
                :items="prefectures"
                item-text="prefecture_social_name"
                item-value="prefecture_id"
                clearable outlined dense hide-details="auto" class="bg-slate-50"
                :disabled="$store.getters.getUserData.table_key == 'prefectures'"
                @change="getProviders(filter.prefecture_id)"
                placeholder="Selecione a prefeitura"
              />
            </v-col>
            <v-col cols="12" sm="12" md="12">
               <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Participante</label>
               <v-autocomplete
                 v-model="filter.provider_id"
                 :items="providers"
                 item-text="provider_name"
                 item-value="provider_id"
                 clearable outlined dense hide-details="auto" class="bg-slate-50"
                 placeholder="Selecione o participante"
               />
            </v-col>
          </v-row>
        </div>
        <div class="px-8 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <v-btn text :loading="loader" v-if="loader"></v-btn>
          <button
            class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/20 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-8 h-10 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-bold uppercase tracking-widest"
            @click="getData"
            :disabled="!(filter.startDate && filter.endDate && filter.prefecture_id)"
          >
            FILTRAR
          </button>
        </div>
      </div>

      <!-- LISTAGEM -->
      <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">ABASTECIMENTOS BLOQUEADOS</h2>
          </div>
          <div class="flex items-center gap-4">
              <v-btn
                text dark
                :disabled="supplies.length === 0"
                target="_blank"
                :to="`/report/master/blocked/${filter.startDate}/${filter.endDate}/${filter.prefecture_id}/${filter.provider_id}`"
                class="text-[11px] tracking-widest font-bold h-10 px-6 rounded-lg"
                color="orange"
              >
                GERAR RELATÓRIO
              </v-btn>
              <button
                v-if="$store.getters.getUserData.table_key !== 'prefectures'"
                @click="dropSupplies"
                :disabled="selectedSupplies.length === 0"
                class="text-[11px] font-bold uppercase tracking-widest text-red-400 hover:text-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-slate-800 px-6 h-10 rounded-lg shadow-md hover:bg-slate-900 transition-all ml-2"
              >
                DELETAR SELECIONADOS
              </button>
          </div>
        </div>
        
        <div class="p-0 text-sm">
          <v-data-table
            :items="supplies"
            :headers="headers"
            :loading="loader"
            :show-select="$store.getters.getUserData.table_key !== 'prefectures'"
            item-key="supply_id"
            v-model="selectedSupplies"
            class="elevation-0"
          >
            <template v-slot:item.datehour="{ item }">
              <span>{{ convertDateTime(item.datehour) }}</span>
            </template>
            <template v-slot:item.supply_liters_stocked="{ item }">
              <span>{{ item.supply_liters_stocked ? realMaskII(item.supply_liters_stocked) : 0 }}</span>
            </template>
            <template v-slot:item.item_billing_price="{ item }">
              <span>{{ item.supply_bidding_price ? realMaskII(item.supply_bidding_price) : 0 }}</span>
            </template>
            <template v-slot:item.total_billing_price="{ item }">
              <span>{{ item.supply_bidding_price && item.supply_liters_stocked ? realMask((item.supply_bidding_price * item.supply_liters_stocked).toFixed(2)) : 0 }}</span>
            </template>
            <template v-slot:item.supply_price_day="{ item }">
              <span>{{ item.supply_price_day ? realMaskII(item.supply_price_day) : 0 }}</span>
            </template>
            <template v-slot:item.supply_value="{ item }">
              <span>{{ item.supply_value ? realMask(item.supply_value.toFixed(2)) : 0 }}</span>
            </template>
          </v-data-table>
        </div>

        <div class="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center gap-6">
          <span class="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Total Dia: <span class="text-slate-900 ml-1">R$ {{ totals.day_value ? realMask(totals.day_value.toFixed(2)) : 0 }}</span></span>
          <span class="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Total Licitado: <span class="text-slate-900 ml-1">R$ {{ totals.bidding_total ? realMask(totals.bidding_total.toFixed(2)) : 0 }}</span></span>
        </div>
      </div>
      
    </div>
    
    <v-snackbar multi-line :color="snack.color" v-model="snack.show" transition="fade-transition">
      {{ snack.message }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Date from "@/components/InputDate.vue";
import Helpers from "@/mixins/helpers";
export default {
  components: { Date },
  mixins: [Helpers],
  data: () => ({
    prefectures: [],
    providers: [],
    items: [],
    selectedSupplies: [],
    filter: {
      prefecture_id: null,
      provider_id: null,
      startDate: null,
      endDate: null,
    },
    totals: {
      bidding_total: 0,
      day_value: 0,
      billing_price: 0,
    },
    supplies: [],
    headers: [
      { text: "Data/Hora", value: "datehour" },
      { text: "Lts Abastecidos", value: "supply_liters_stocked" },
      { text: "Preço Licitado", value: "supply_bidding_price" },
      { text: "T. Licitado", value: "total_billing_price" },
      { text: "Preço do dia", value: "supply_price_day" },
      { text: "Total", value: "supply_value" },
      { text: "Senha", value: "supply_code" },
      { text: "Status", value: "supply_status" },
      { text: "Número do Cupom", value: "supply_coupon" },
      { text: "Motorista", value: "driver_name" },
    ],
    loader: false,
    snack: {
      show: false,
      message: "",
      color: "",
    },
  }),
  created() {
    if(this.$store.getters.getUserData.table_key == 'prefectures') {
      this.filter.prefecture_id = this.$store.getters.getUserData.table_id
      this.getProviders(this.filter.prefecture_id);
    }
    this.Helpers;
    this.loader = true;
    this.$store.dispatch("setLoader", this.loader);
    this.$http.get(`/prefectures`).then((r) => {
      this.prefectures = r.data;
      this.loader = false;
      this.$store.dispatch("setLoader", this.loader);
    });
  },
  methods: {
    getProviders(id) {
      this.$http.get(`/providers/prefecture/${id}`).then((r) => {
        this.providers = r.data;
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
      });
    },
    dropSupplies() {
      let c = confirm("Deseja excluir os abastecimentos selecionados?");
      if (c) {
        this.loader = true;
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .post("/supplies/blocked/delete", {
            selectedSupplies: this.selectedSupplies,
          })
          .then((r) => {
            this.snack.message = r.data.result;
            this.loader = false;
            this.getData();
            this.snack.color = "success";
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.loader = false;
            this.snack.show = true;
            this.snack.message = e;
            this.snack.type = "error";
            this.$store.dispatch("setLoader", this.loader);
          });
      }
    },
    getData() {
      if (this.filter.prefecture_id) {
        this.totals.day_value = 0;
        this.totals.bidding_total = 0;
        this.supplies = [];
        this.loader = true;
        this.$store.dispatch("setLoader", this.loader);
        let liters = 0;
        let bidding_price = 0;
        let supply_price_day = 0;
        this.$http
          .post(`/supplies/blocked`, this.filter)
          .then((supplies) => {
            this.supplies = supplies.data;
            supplies.data.map((supply,index) => {
              liters = supply.supply_liters_stocked.toFixed(3);
              bidding_price = supply.supply_bidding_price.toFixed(3);
              supply_price_day = supply.supply_price_day.toFixed(3);
              this.supplies[index].supply_liters_stocked = liters;
              this.supplies[index].supply_bidding_price = bidding_price;
              this.supplies[index].supply_price_day = supply_price_day;
              this.totals.day_value += parseFloat(supply.supply_value);
              this.totals.bidding_total += parseFloat(
                supply.supply_bidding_value
              );
            });
            this.loader = false;
            this.snack.color = "success";
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.loader = false;
            this.snack.show = true;
            this.snack.message = e;
            this.snack.type = "error";
            this.$store.dispatch("setLoader", this.loader);
          });
      }
    },
  },
};
</script>

<style>
</style>